import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  KeyRound, Webhook, Copy, RefreshCw, Trash2, Plus, Send, ShieldCheck, AlertCircle,
} from 'lucide-react';

const copy = (text) => {
  navigator.clipboard.writeText(text);
  toast.success('Copied to clipboard');
};

const SecretReveal = ({ open, onClose, title, secretLabel, secret, note }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="awp-secret-dialog">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription className="flex items-center gap-2 text-amber-600">
          <AlertCircle className="h-4 w-4" /> {note}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label>{secretLabel}</Label>
        <div className="flex gap-2">
          <Input readOnly value={secret || ''} className="font-mono text-xs" data-testid="awp-secret-value" />
          <Button type="button" variant="outline" size="icon" onClick={() => copy(secret)} data-testid="awp-secret-copy">
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={onClose} data-testid="awp-secret-done">Done</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default function AWPIntegrationSettings({ api }) {
  // ---- Credentials ----
  const [creds, setCreds] = useState([]);
  const [credName, setCredName] = useState('');
  const [creatingCred, setCreatingCred] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [secretModal, setSecretModal] = useState(null); // {title, secretLabel, secret, note}

  // ---- Webhooks ----
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [showHookForm, setShowHookForm] = useState(false);
  const [hookUrl, setHookUrl] = useState('');
  const [hookEvents, setHookEvents] = useState([]);
  const [hookDesc, setHookDesc] = useState('');
  const [savingHook, setSavingHook] = useState(false);
  const [deleteHook, setDeleteHook] = useState(null);
  const [testingId, setTestingId] = useState(null);

  const loadCreds = useCallback(async () => {
    try {
      const r = await api.get('/v1/awp-admin/credentials');
      setCreds(r.data.items || []);
    } catch (e) { if (e.response?.status !== 403) console.error('AWP loadCreds', e); }
  }, [api]);

  const loadHooks = useCallback(async () => {
    try {
      const r = await api.get('/v1/awp-admin/webhooks');
      setHooks(r.data.items || []);
      setEvents(r.data.available_events || []);
    } catch (e) { if (e.response?.status !== 403) console.error('AWP loadHooks', e); }
  }, [api]);

  useEffect(() => { loadCreds(); loadHooks(); }, [loadCreds, loadHooks]);

  const createCred = async () => {
    setCreatingCred(true);
    try {
      const r = await api.post('/v1/awp-admin/credentials', { name: credName || 'AWP' });
      setSecretModal({
        title: 'Credential created',
        secretLabel: `Client Secret  (client_id: ${r.data.client_id})`,
        secret: r.data.client_secret,
        note: r.data.notice || 'Store this secret now — it cannot be retrieved again.',
      });
      setCredName('');
      loadCreds();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create credential');
    } finally { setCreatingCred(false); }
  };

  const rotateCred = async (clientId) => {
    try {
      const r = await api.post(`/v1/awp-admin/credentials/${clientId}/rotate`);
      setSecretModal({
        title: 'Secret rotated',
        secretLabel: `New Client Secret  (client_id: ${clientId})`,
        secret: r.data.client_secret,
        note: r.data.notice || 'Previous tokens are now invalid.',
      });
      loadCreds();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to rotate'); }
  };

  const revokeCred = async () => {
    try {
      await api.post(`/v1/awp-admin/credentials/${revokeTarget}/revoke`);
      toast.success('Credential revoked');
      setRevokeTarget(null);
      loadCreds();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to revoke'); }
  };

  const saveHook = async () => {
    setSavingHook(true);
    try {
      const r = await api.post('/v1/awp-admin/webhooks', {
        url: hookUrl, events: hookEvents, description: hookDesc || null,
      });
      setSecretModal({
        title: 'Webhook created',
        secretLabel: 'Signing Secret (verify the X-AWP-Signature header)',
        secret: r.data.secret,
        note: r.data.notice || 'Store this signing secret now — it cannot be retrieved again.',
      });
      setShowHookForm(false);
      setHookUrl(''); setHookEvents([]); setHookDesc('');
      loadHooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create webhook');
    } finally { setSavingHook(false); }
  };

  const toggleHookStatus = async (h) => {
    try {
      await api.patch(`/v1/awp-admin/webhooks/${h.id}`, {
        status: h.status === 'active' ? 'paused' : 'active',
      });
      loadHooks();
    } catch (e) { toast.error('Failed to update webhook'); }
  };

  const rotateHook = async (id) => {
    try {
      const r = await api.post(`/v1/awp-admin/webhooks/${id}/rotate-secret`);
      setSecretModal({
        title: 'Signing secret rotated', secretLabel: 'New Signing Secret',
        secret: r.data.secret, note: r.data.notice,
      });
    } catch (e) { toast.error('Failed to rotate secret'); }
  };

  const testHook = async (id) => {
    setTestingId(id);
    try {
      const r = await api.post(`/v1/awp-admin/webhooks/${id}/test`);
      if (r.data.delivered) toast.success('Test event delivered (HTTP ' + r.data.status_code + ')');
      else toast.error('Delivery failed: ' + (r.data.error || r.data.status_code || 'unknown'));
      loadHooks();
    } catch (e) { toast.error('Test failed'); } finally { setTestingId(null); }
  };

  const removeHook = async () => {
    try {
      await api.delete(`/v1/awp-admin/webhooks/${deleteHook}`);
      toast.success('Webhook deleted');
      setDeleteHook(null);
      loadHooks();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const toggleEvent = (ev) =>
    setHookEvents((prev) => prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]);

  return (
    <div className="space-y-6" data-testid="awp-integration-settings">
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
        <ShieldCheck className="h-5 w-5 text-blue-600 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-900">Autonomous Workforce Platform (AWP)</p>
          <p className="text-sm text-blue-700">
            Manage the service credentials AWP uses to read data and create bookings, and the webhook
            endpoints that receive callbacks. Available on demo tenants only.
          </p>
        </div>
      </div>

      {/* -------- Credentials -------- */}
      <Card data-testid="awp-credentials-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Service Credentials</CardTitle>
          <CardDescription>
            AWP exchanges a client_id + secret for a 15-minute access token. The secret is shown only once.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label htmlFor="cred-name">New credential name</Label>
              <Input id="cred-name" value={credName} onChange={(e) => setCredName(e.target.value)}
                placeholder="e.g. AWP Production" data-testid="awp-cred-name-input" />
            </div>
            <Button onClick={createCred} disabled={creatingCred} data-testid="awp-cred-create-btn">
              <Plus className="h-4 w-4 mr-2" /> Mint credential
            </Button>
          </div>

          <div className="space-y-2">
            {creds.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="awp-creds-empty">No credentials yet.</p>
            )}
            {creds.map((c) => (
              <div key={c.client_id} className="flex items-center justify-between rounded-md border p-3"
                data-testid={`awp-cred-row-${c.client_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate">{c.client_id}</p>
                  <p className="text-xs text-muted-foreground">{(c.scopes || []).length} scopes · v{c.version}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={c.status === 'active' ? 'default' : 'secondary'}>{c.status}</Badge>
                  {c.status === 'active' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => rotateCred(c.client_id)}
                        data-testid={`awp-cred-rotate-${c.client_id}`}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setRevokeTarget(c.client_id)}
                        data-testid={`awp-cred-revoke-${c.client_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* -------- Webhooks -------- */}
      <Card data-testid="awp-webhooks-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Webhook className="h-5 w-5" /> Webhooks</CardTitle>
          <CardDescription>
            Receive a signed POST when a booking is approved or an allocation changes. Verify the
            <code className="mx-1">X-AWP-Signature</code> header (HMAC-SHA256 of the raw body) with your signing secret.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!showHookForm ? (
            <Button variant="outline" onClick={() => setShowHookForm(true)} data-testid="awp-hook-add-btn">
              <Plus className="h-4 w-4 mr-2" /> Add endpoint
            </Button>
          ) : (
            <div className="space-y-3 rounded-md border p-4" data-testid="awp-hook-form">
              <div>
                <Label htmlFor="hook-url">Endpoint URL</Label>
                <Input id="hook-url" value={hookUrl} onChange={(e) => setHookUrl(e.target.value)}
                  placeholder="https://awp.example.com/callbacks/equipr" data-testid="awp-hook-url-input" />
              </div>
              <div>
                <Label>Events <span className="text-xs text-muted-foreground">(none selected = all)</span></Label>
                <div className="mt-2 space-y-2">
                  {events.map((ev) => (
                    <label key={ev} className="flex items-center gap-2 text-sm cursor-pointer"
                      data-testid={`awp-hook-event-${ev}`}>
                      <Checkbox checked={hookEvents.includes(ev)} onCheckedChange={() => toggleEvent(ev)} />
                      <code>{ev}</code>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="hook-desc">Description (optional)</Label>
                <Input id="hook-desc" value={hookDesc} onChange={(e) => setHookDesc(e.target.value)}
                  data-testid="awp-hook-desc-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={saveHook} disabled={savingHook || !hookUrl} data-testid="awp-hook-save-btn">Save</Button>
                <Button variant="ghost" onClick={() => setShowHookForm(false)} data-testid="awp-hook-cancel-btn">Cancel</Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {hooks.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="awp-hooks-empty">No webhook endpoints yet.</p>
            )}
            {hooks.map((h) => (
              <div key={h.id} className="rounded-md border p-3" data-testid={`awp-hook-row-${h.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-mono truncate">{h.url}</p>
                    <p className="text-xs text-muted-foreground">
                      {(h.events && h.events.length) ? h.events.join(', ') : 'all events'}
                      {h.last_delivery_status && ` · last: ${h.last_delivery_status}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1">
                      <Switch checked={h.status === 'active'} onCheckedChange={() => toggleHookStatus(h)}
                        data-testid={`awp-hook-toggle-${h.id}`} />
                      <span className="text-xs text-muted-foreground">{h.status}</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => testHook(h.id)} disabled={testingId === h.id}
                      data-testid={`awp-hook-test-${h.id}`}><Send className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => rotateHook(h.id)}
                      data-testid={`awp-hook-rotate-${h.id}`}><RefreshCw className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteHook(h.id)}
                      data-testid={`awp-hook-delete-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {secretModal && (
        <SecretReveal open onClose={() => setSecretModal(null)} {...secretModal} />
      )}

      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent data-testid="awp-revoke-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke credential?</AlertDialogTitle>
            <AlertDialogDescription>
              Any AWP tokens issued from this credential will stop working immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={revokeCred} data-testid="awp-revoke-confirm-btn">Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteHook} onOpenChange={(o) => !o && setDeleteHook(null)}>
        <AlertDialogContent data-testid="awp-hook-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete webhook endpoint?</AlertDialogTitle>
            <AlertDialogDescription>AWP will no longer receive callbacks at this URL.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeHook} data-testid="awp-hook-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
